import {
    isPlayerAction,
    isRemovePlayerEvent,
    PlayerId,
    Projection,
    SharedGameState,
    useGameStore,
} from '@glark-newco/game-library';
import dayjs, {Dayjs} from 'dayjs';
import {shallow} from 'zustand/shallow';
import {isAssignPlayerNodePayload, isSaveObjectivePayload, RingColour, RingId} from '../SupplyChainPayloadTypes';

export interface Objective {
    ringColour: RingColour;
    ringId: RingId | undefined;
    completionTime?: Dayjs;
}

export type ObjectivesMap = Record<PlayerId, Objective[]>;

export interface PlayerObjectivesSlice extends SharedGameState {
    playerObjectives?: ObjectivesMap;
}

export const objectivesProjection: Projection<PlayerObjectivesSlice> = {
    name: 'objectivesProjection',
    project: (state, newEvents) => {
        const objectives = {...(state.playerObjectives ?? {})};
        let modified = false;

        newEvents.forEach(ev => {
            if (isPlayerAction(ev) && isAssignPlayerNodePayload(ev.payload)) {
                const assign = ev.payload;
                objectives[assign.playerId] = assign.objective.map(colour => ({ringColour: colour, ringId:undefined}));
                modified = true;
            }  else if (isPlayerAction(ev) && isSaveObjectivePayload(ev.payload)) {
                const objectivePayload = ev.payload;
                if (!objectives[objectivePayload.playerId]) // In normal gameplay, this is impossible.
                    objectives[objectivePayload.playerId] = [];
                const playerObjectives = [...objectives[objectivePayload.playerId]]; // clone to force re-render
                const activeObjective = playerObjectives.findIndex(o => (o.ringId == undefined));
                // console.log('Applying objective for player:', objectivePayload.playerId, 'ring:', objectivePayload.ringId, 'activeObjective:', activeObjective);
                playerObjectives[activeObjective] = {
                    ...playerObjectives[activeObjective],
                    ringId:objectivePayload.ringId,
                    completionTime: dayjs(ev.timestamp),
                };
                objectives[objectivePayload.playerId] = playerObjectives;
                modified = true;
            } else if (isRemovePlayerEvent(ev)) {
                if (ev.replacementId) {
                    const removedId = ev.removePlayerId;
                    const replacementId = ev.replacementId;
                    const removedObjectives = objectives[removedId] ?? [];

                    if (removedObjectives.length) {
                        const existingObjectives = objectives[replacementId] ?? [];
                        objectives[replacementId] = [
                            ...existingObjectives,
                            ...removedObjectives,
                        ];
                    }
                    delete objectives[removedId];
                    modified = true;
                }
            }
        });

        return modified ? {playerObjectives: objectives} : {};
    },
    reset: () => ({playerObjectives: {}}),
};

export const usePlayerObjectives = (playerId: PlayerId): Objective[] => {
    return useGameStore(
        (s: PlayerObjectivesSlice) => s.playerObjectives?.[playerId] ?? [],
        shallow,
    );
};

export const useObjectivesMap = (): ObjectivesMap =>
    useGameStore((s: PlayerObjectivesSlice) => s.playerObjectives) ?? {};
